import React, { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AddIconCircle } from 'assets';
import { Input, RadioInput, Textarea } from 'components/form';
import * as yup from 'yup';
import { BaseEditModal } from '../base-edit-modal';
import { toTitleCase } from 'utils/titleCase';

type ResponsibilityFormData = {
  responsibilities: {
    title: string;
    description: string;
    checked: boolean;
  }[];
};

const responsibilitySchema = yup.object().shape({
  responsibilities: yup.array().of(
    yup.object().shape({
      title: yup.string().required('Title is required'),
      description: yup.string().required('Description is required'),
      checked: yup.boolean().default(false),
    })
  ).required('At least one responsibility is required').min(1, 'At least one responsibility is required'),
});

interface CoreResponsibilitiesEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: ResponsibilityFormData;
  onSave: (data: ResponsibilityFormData) => void;
  isSubmitting?: boolean;
  title?: string;
}

export const CoreResponsibilitiesEditModal: React.FC<CoreResponsibilitiesEditModalProps> = ({
  isOpen,
  onClose,
  currentData,
  onSave,
  isSubmitting,
  title = "Core Responsibilities"
}) => {
  const defaultValues: ResponsibilityFormData = currentData || { responsibilities: [] };

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch
  } = useForm<ResponsibilityFormData>({
    defaultValues,
    resolver: yupResolver(responsibilitySchema)
  });

  const { fields, append } = useFieldArray({
    control,
    name: 'responsibilities'
  });

  useEffect(() => {
    reset(defaultValues);
  }, [currentData, reset]);

  const onSubmit = (data: ResponsibilityFormData) => {
    onSave(data);
  };

  const addResponsibility = () => {
    append({ title: '', description: '', checked: true });
  };

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      contentClassName="!bg-white"
      saveButtonText="Update"
    >
      <div className="font-[Gelion] w-[88%] mx-auto bg-white rounded-lg py-4">
        <h3 className="text-lg font-semibold text-[#1D2939] mb-2.5">Key Responsibilities</h3>
        {fields.map((item, index) => (
          <div
            key={item.id}
            className="relative mb-4 border-b border-[#E4E7EC] last:border-b-0 last:mb-0 last:pb-0 [&_label]:text-[#667085] [&_label]:text-sm [&_label]:font-medium pt-3"
          >
            <RadioInput
              register={register}
              name={`responsibilities.${index}.checked`}
              validatorMessage={errors.responsibilities?.[index]?.checked?.message}
              options={[{
                label: '',
                description: '',
                value: 'true',
              }]}
              type="checkbox"
              parentClassName="grid gap-[5%] !mb-2 !absolute top-0 right-0 !w-fit z-10"
              labelClassName="!mb-1 !w-fit"
              className='!w-fit'
            />
            <div>
              <Input
                placeholder=''
                label="Title"
                type="text"
                required
                validatorMessage={errors.responsibilities?.[index]?.title?.message ?? ""}
                name={`responsibilities.${index}.title`}
                register={register}
                className="!border !border-[#01216980] !rounded-md !bg-white"
                parentClassName='!mb-2'
              />
            </div>

            <div>
              <Textarea
                placeholder=''
                label="Subtext"
                required
                validatorMessage={errors.responsibilities?.[index]?.description?.message ?? ""}
                name={`responsibilities.${index}.description`}
                register={register}
                className="!border !border-[#01216980] !rounded-md !bg-white !h-[70px] !min-h-[56px]"
                parentClassName='!mb-4'
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className="mt-4 px-6 py-2 bg-[var(--primary-blue)] text-white font-medium border-none rounded cursor-pointer uppercase transition-colors duration-200 flex items-center gap-1.5 hover:bg-[#003087]"
          onClick={addResponsibility}
        >
          <AddIconCircle className='' /> Add Responsibility
        </button>
      </div>
    </BaseEditModal>
  );
};
